import React, { useState, createContext, useContext } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { corporateTheme, corporateLabels } from '../themes/corporate';

// Create Voice Chat Context
const VoiceChatContext = createContext();

export const useVoiceChat = () => {
  const context = useContext(VoiceChatContext);
  if (!context) {
    throw new Error('useVoiceChat must be used within a VoiceChatProvider');
  }
  return context;
};

const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${corporateTheme.primaryGradient};
  border: none;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  box-shadow: ${corporateTheme.cardShadow};
  transition: all 0.3s ease;
  color: ${corporateTheme.background};
  font-weight: 600;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 35px rgba(30, 64, 175, 0.4);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
  
  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 56px;
    height: 56px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    bottom: 1rem;
    right: 1rem;
    width: 52px;
    height: 52px;
    font-size: 0.8rem;
  }
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: ${corporateTheme.textPrimary};
  color: ${corporateTheme.background};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  font-family: ${corporateTheme.fontFamily};
  
  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: ${corporateTheme.textPrimary};
  }
  
  @media (max-width: 768px) {
    right: 65px;
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
  
  @media (max-width: 480px) {
    right: 60px;
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
    
    &::after {
      border-width: 5px;
    }
  }
`;

const PulseRing = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border: 2px solid ${corporateTheme.primary};
  border-radius: 50%;
  opacity: 0.6;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ConfirmModal = styled(motion.div)`
  background: ${corporateTheme.background};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  width: 100%;
  max-width: 450px;
  padding: 2rem;
  box-shadow: ${corporateTheme.cardShadow};
  border: 1px solid ${corporateTheme.cardBorder};
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 1.5rem;
  }
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid ${corporateTheme.primary};
  object-fit: cover;
  box-shadow: 0 8px 25px rgba(30, 64, 175, 0.3);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ModalDescription = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  line-height: 1.5;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${corporateTheme.primary};
  font-weight: 600;
  margin-top: 0.5rem;
  
  .status-dot {
    width: 8px;
    height: 8px;
    background: ${corporateTheme.primary};
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const VoiceButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 150px;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(30, 64, 175, 0.3);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
`;

const CancelButton = styled(motion.button)`
  background: transparent;
  color: ${corporateTheme.textTertiary};
  border: 2px solid ${corporateTheme.cardBorder};
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    border-color: ${corporateTheme.textSecondary};
    color: ${corporateTheme.textSecondary};
    transform: translateY(-2px);
  }
`;

// Voice Chat Modal Component
const VoiceChatModal = () => {
  const { isModalOpen, closeModal } = useVoiceChat();

  const openVoiceChat = () => {
    // Open ElevenLabs voice chat in a new window/tab
    const voiceChatUrl = 'https://elevenlabs.io/app/talk-to?agent_id=agent_01jytkkwg4erpaww5m1se8nqj1';
    
    // Check if it's a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, open in the same tab for better UX
      window.location.href = voiceChatUrl;
    } else {
      // On desktop, open in a new tab
      const newWindow = window.open(voiceChatUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      if (newWindow) {
        newWindow.focus();
      } else {
        // Fallback if popup is blocked
        window.location.href = voiceChatUrl;
      }
    }
    
    closeModal();
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <ConfirmModal
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ProfileSection>
              <ProfileImage 
                src="https://images.unsplash.com/photo-1494790108755-2616b612b3be?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
                alt="Sofia - Affiliate Specialist"
                onError={(e) => {
                  e.target.src = "https://ui-avatars.com/api/?name=Sofia&background=1e40af&color=ffffff&size=150&bold=true";
                }}
              />
              <div>
                <ModalTitle>
                  Sofia - {corporateLabels.affiliate} Specialist
                </ModalTitle>
                <ModalDescription>
                  Ready to discuss commission rates, earning opportunities, and answer your affiliate questions via voice chat.
                </ModalDescription>
                <StatusIndicator>
                  <div className="status-dot"></div>
                  Online • Ready to help
                </StatusIndicator>
              </div>
            </ProfileSection>
            
            <ButtonGroup>
              <VoiceButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openVoiceChat}
              >
                {corporateLabels.expert} Voice Chat
              </VoiceButton>
              
              <CancelButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={closeModal}
              >
                Cancel
              </CancelButton>
            </ButtonGroup>
          </ConfirmModal>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

// Voice Chat Provider Component
export const VoiceChatProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <VoiceChatContext.Provider value={{
      isModalOpen,
      openModal,
      closeModal
    }}>
      {children}
      <VoiceChatModal />
    </VoiceChatContext.Provider>
  );
};

// Floating Voice Chat Button Component
const VoiceChatButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const { openModal, isModalOpen } = useVoiceChat();

  return (
    <>
      <FloatingButton
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={openModal}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ 
          type: "spring", 
          stiffness: 260, 
          damping: 20,
          delay: 2 
        }}
      >
        CHAT
        
        <PulseRing
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.6, 0, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </FloatingButton>
      
      <AnimatePresence>
        {isHovered && !isModalOpen && (
          <Tooltip
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            Talk to Sofia - Voice Chat
          </Tooltip>
        )}
      </AnimatePresence>
    </>
  );
};

export default VoiceChatButton; 